module API.Pagination
    ( PageQuery, PageQueryBase, PageQueryExtractor
    , Page, pageRec, pageItems, pageNumber, pageLast
    , pageQuery, pageQuery', changePage
    , pageInfoFields
    , Unpaged, unpaged
    , fromTestPage
    ) where

import Common

import Data.Argonaut (class DecodeJson, class EncodeJson, Json, decodeJson, jsonNull, (.:))
import Data.Argonaut as J
import Data.Either (Either)
import Data.Int (toNumber)
import Data.Maybe (Maybe(..))
import Effect.Aff (Aff)
import Foreign.Object as O
import GraphQL (query)
import GraphQL.Query (class QueryResult, Query, QueryVar(..), queryResultDecode, queryResultShape)
import GraphQL.Shape (class HasShape, QueryField(..), shapeOf)
import GraphQL.Type (QType(..))
import Type.Prelude (Proxy(..))

-- | This contains the query string necessary to query a `PaginatorInfo` object
-- | with fields needed by `pageQuery` and must be included in query strings
-- | passed to it.
pageInfoFields :: String
pageInfoFields = """
    paginatorInfo {
        currentPage
        lastPage
    }
"""

type PaginatorInfo =
    { currentPage :: Int
    , lastPage :: Int
    }

-- | Type representing a paginated collection loaded from the backend
-- |
-- | To query the initial page, simply use this as a return type from
-- | `buildQuery` instead of an `Array`. The query generator will automatically
-- | handle querying information necessary for pagination and return one of
-- | these.
data Page a = Page (PageRec a)

instance functorPage :: Functor Page where
    map f (Page page) = Page $ page { items = map f page.items }

-- | Represents a single page in a paginated collection.
type PageRec a =
    { items :: Array a
    , number :: Int
    , lastPage :: Int
    , query :: PageQuery
    }
type PageQueryBase r =
    { qstr :: String
    , extract :: PageQueryExtractor
    | r
    }
type PageQuery = PageQueryBase (vars :: Json)
type PageQueryExtractor = Json -> Either ErrorWrapper Json

pageRec :: forall a. Page a -> PageRec a
pageRec (Page p) = p

-- | List items on this page
pageItems :: forall a. Page a -> Array a
pageItems (Page { items }) = items

-- | Get this page's number
pageNumber :: forall a. Page a -> Int
pageNumber (Page { number }) = number

-- | Get the number of the last page in this collection
pageLast :: forall a. Page a -> Int
pageLast (Page { lastPage }) = lastPage

-- | Query the backend for the given page number in this page's collection and return it as a new `Page`
-- |
-- | This will re-run the initial query that loaded this page (which is stored
-- | internally in the `Page` object) with a different `page` argument and wrap
-- | the result into a new `Page`.
changePage :: forall a. DecodeJson a => Int -> Page a -> Aff (Either ErrorWrapper (Page a))
changePage pageNo (Page page) =
    (decodePage page.query =<< _) <$> query q (withPageNo pageNo vars)
  where q = page.query.qstr
        vars = page.query.vars


-- | Deprecated: use `buildQuery` instead
pageQuery :: forall item vars. DecodeJson item => EncodeJson vars =>
    String -> String -> vars -> Aff (Either ErrorWrapper ((Page item)))
pageQuery qname = pageQuery' $ nameExtractor qname

-- | Deprecated: use `buildQuery` instead
pageQuery' :: forall item vars. DecodeJson item => EncodeJson vars =>
    PageQueryExtractor
    -> String
    -> vars
    -> Aff (Either ErrorWrapper (Page item))
pageQuery' extract qstr vars' =
    (decodePage pq =<< _) <$> query qstr (withPageNo 1 vars)
  where
    pq = { extract, qstr, vars }
    vars = J.encodeJson vars'


-- Instance allowing pages to be used as a result for `buildQuery`
instance pageQR :: (DecodeJson item, HasShape item) => QueryResult (Page item) where
    -- Decode by constructing a page query and passing it to the `decodePage_` function
    queryResultDecode { query: { name }, qstr, vars } = decodePage_ pq
      where
        pq = { extract: nameExtractor name, qstr, vars }
    -- Query the paginator info and the inner data with the item's shape
    queryResultShape _ =
        [ QObject "paginatorInfo" $ shapeOf (Proxy :: Proxy PaginatorInfo)
        , QObject "data" $ shapeOf (Proxy :: Proxy item) ]
    -- Inject the `page` argument into the generated query. It's set to null
    -- for now, but when `changePage` is called it will be put into the JSON to
    -- request a different page.
    queryResultArgs _ = [ Tuple (QueryVar "page" $ Nullable $ QType "Int") $ jsonNull ]


-- | Use this with `buildQuery` to ignore pagination for a GraphQL query that is paginated on the backend
-- |
-- | Example:
-- | ```
-- | unpaged $ buildQuery "queryName" {}
-- | ```
-- |
-- | This works by causing the `buildQuery` function to infer the return type
-- | as a special `Unpaged` newtype, and then unwrapping that newtype into the
-- | inner type transparently within this function.
unpaged :: forall a. Query (Unpaged (Array a)) -> Query (Array a)
unpaged = map (\(Unpaged r) -> r)

-- | Dummy newtype wrapper allowing `buildQuery` to access the first page of
-- | paginated queries on the backend while ignoring the actual pagination.
data Unpaged a = Unpaged a

instance unpagedQR :: (QueryResult (Array inner)) => QueryResult (Unpaged (Array inner)) where
    -- Decode the inner query result from the `data` field. Ignore the `paginatorInfo`
    queryResultDecode qrc json = do
        inner <- wrapJsonError' json (decodeJObject json >>= (_ .: "data"))
        map Unpaged $ queryResultDecode qrc inner
    -- Request the shape of the inner object from the `data` field. Ignore the `paginatorInfo`
    queryResultShape _ = [ QObject "data" $ queryResultShape (Proxy :: Proxy (Array inner)) ]
    queryResultArgs _ = []


nameExtractor :: String -> PageQueryExtractor
nameExtractor name json = wrapJsonError' json $ decodeJObject json >>= \obj ->
    obj .: "data" >>= (_ .: name)


-- | Internal function. Decodes the result of a paginated query into a `Page`.
decodePage :: forall item. DecodeJson item =>
    PageQuery -> Json -> Either ErrorWrapper (Page item)
decodePage pq json = pq.extract json >>= decodePage_ pq

decodePage_ :: forall item. DecodeJson item =>
    PageQuery -> Json -> Either ErrorWrapper (Page item)
decodePage_ pq json = wrapJsonError' json $ do
    obj <- decodeJObject json
    pinfo :: PaginatorInfo <- decodeJson =<< (obj .: "paginatorInfo")
    items <- decodeJson =<< (obj .: "data")
    pure $ Page
        { items: items
        , number: pinfo.currentPage
        , lastPage: pinfo.lastPage
        , query: pq
        }


-- | Takes a `Json` object and inserts a `page` field with the given value.
withPageNo :: Int -> Json -> Json
withPageNo page json =
    case J.toObject json of
         Just obj -> J.fromObject $ O.insert "page" (J.fromNumber $ toNumber page) obj
         Nothing -> json


-- | Page used for testing.
fromTestPage :: forall a. Array a -> Page a
fromTestPage items = Page
    { items, number: 3, lastPage: 10
    , query:
        { qstr: ""
        , vars: jsonNull
        , extract: \_ -> Left $ StringError "TEST PAGE -- no query"
        }
    }
